import { Button } from '@/lib/Buttons';
import { Audience, AudienceSyncTimeBasedSchedule } from '@/models/audiences';
import { RootState, useAppDispatch } from '@/reducers';
import { useSaveAudienceMutation, useUpdateAudienceMutation, useUpdateAudienceScheduleMutation } from '@/services/endpoints/audiences';
import { useGetTraitsQuery } from '@/services/endpoints/traits';
import { FormikProps } from 'formik';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useWorkspace } from '../workspaces/hooks';
import { clearStashedAudience, stashAudience } from './audienceEditSlice';
import { AudienceContent, AudienceFormModel } from './components';
import { AudienceSyncFormModel } from './components/sync/AudienceSyncContainer';
import { AUDIENCE_EDIT_STEPS } from './constants';
import { IModalContentProps } from '@/models/modals';
import { AudienceModalContent } from './components/AudienceModalContent';
import { useCreateStepper } from '@/lib/Steppers';

type AudienceEditProps = {
  onSuccess: (audience: Audience) => void;
} & IModalContentProps;

export const AudienceEditV2: FC<AudienceEditProps> = ({ onSuccess, closeModal }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('audience_edit');

  const createFormRef = useRef<FormikProps<AudienceFormModel>>(null);
  const syncFormRef = useRef<FormikProps<AudienceSyncFormModel>>(null);

  const { steps, currentStepId, currentStepIndex, goToNextStep, isLastStep, completeCurrentStep } = useCreateStepper(AUDIENCE_EDIT_STEPS);
  const workspace = useWorkspace();

  const stashedAudience = useSelector((state: RootState) => state.audienceEdit.audience);

  const audienceForForm = currentStepId === 'nameAndMetadata' ? undefined : stashedAudience;

  const { data: traits } = useGetTraitsQuery({
    workspaceId: workspace.id,
  });

  const [saveAudience, { isLoading: isSaveLoading, data: createdAudience, isSuccess: isSaveSuccess }] = useSaveAudienceMutation();
  const [updateAudience, { isLoading: isUpdateLoading, data: updatedAudience, isSuccess: isUpdateSuccess }] = useUpdateAudienceMutation();
  const [updateAudienceSchedule, { isLoading: isUpdateScheduleLoading, data: updatedAudienceSchedule, isSuccess: isUpdateScheduleSuccess }] =
    useUpdateAudienceScheduleMutation();

  const isLoading = isSaveLoading || isUpdateLoading || isUpdateScheduleLoading;

  const isModal = !!closeModal;

  // Clear stashed audience on component mount to start fresh
  useEffect(() => {
    dispatch(clearStashedAudience());
  }, [dispatch]);

  useEffect(() => {
    if (createdAudience) {
      dispatch(stashAudience(createdAudience));
    }
  }, [createdAudience, dispatch]);

  useEffect(() => {
    if (updatedAudience) {
      dispatch(stashAudience(updatedAudience));
    }
  }, [updatedAudience, dispatch]);

  useEffect(() => {
    if (updatedAudienceSchedule) {
      dispatch(stashAudience(updatedAudienceSchedule));
    }
  }, [updatedAudienceSchedule, dispatch]);

  const createAudience = (audience: Partial<Audience>) => {
    if (audience.datasourceIds && audience.matchingTrait && audience.name) {
      saveAudience({
        workspaceId: workspace.id,
        audience: {
          name: audience.name,
          matchingTrait: audience.matchingTrait,
          datasourceIds: audience.datasourceIds,
        },
      });
    }
  };

  const handleSubmit = async (form: AudienceFormModel) => {
    if (form.name && form.matchingTrait && form.datasourceIds) {
      dispatch(stashAudience({ ...form }));
    }
  };

  const updateSchedule = (audienceId: string, syncForm: AudienceSyncFormModel) => {
    const syncData: AudienceSyncFormModel = { ...syncForm };
    let scheduleConfiguration: AudienceSyncTimeBasedSchedule;

    switch (syncForm.scheduleType) {
      case 'REALTIME':
        syncData.scheduleConfiguration = {};
        break;

      case 'MANUAL':
        syncData.scheduleConfiguration = {
          emailNotification: syncForm.scheduleConfiguration.emailNotification || false,
          createSnapshotOnRefresh: syncForm.scheduleConfiguration.createSnapshotOnRefresh || false,
        };
        break;

      case 'SCHEDULED':
        scheduleConfiguration = {
          ...(syncForm.scheduleConfiguration as AudienceSyncTimeBasedSchedule),
          emailNotification: syncForm.scheduleConfiguration.emailNotification || false,
          createSnapshotOnRefresh: syncForm.scheduleConfiguration.createSnapshotOnRefresh || false,
        };

        switch (scheduleConfiguration.unit) {
          case 'HOURS':
          case 'DAYS':
            delete scheduleConfiguration.weekDays;
            delete scheduleConfiguration.dayOfMonth;
            break;

          case 'WEEKS':
            delete scheduleConfiguration.dayOfMonth;
            break;

          case 'MONTHS':
            delete scheduleConfiguration.weekDays;
            break;
        }

        syncData.scheduleConfiguration = scheduleConfiguration;
        break;
    }

    updateAudienceSchedule({
      workspaceId: workspace.id,
      audienceId,
      audience: syncData,
    });
  };

  const completeAndGoNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handleNextButtonClicked = async () => {
    if (currentStepId === 'nameAndMetadata') {
      await createFormRef.current?.submitForm();
      const audienceForm = createFormRef.current?.values;
      if (createFormRef.current?.isValid && audienceForm) {
        createAudience(audienceForm);
      }
      return;
    }

    if (currentStepId === 'expression') {
      if (stashedAudience?.id && stashedAudience?.name && stashedAudience?.expression) {
        updateAudience({
          workspaceId: workspace.id,
          audienceId: stashedAudience?.id,
          audience: {
            name: stashedAudience.name,
            expression: stashedAudience.expression,
          },
        });
      }
      return;
    }

    if (currentStepId === 'sync') {
      await syncFormRef.current?.submitForm();
      const syncForm = syncFormRef.current?.values;

      if (stashedAudience?.id && syncFormRef.current?.isValid && syncForm) {
        updateSchedule(stashedAudience?.id, syncForm);
      }
      return;
    }

    if (currentStepId === 'confirmation') {
      // closeModal();
      return;
    }

    completeAndGoNextStep();
  };

  useEffect(() => {
    if (isSaveSuccess) {
      completeAndGoNextStep();
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      completeAndGoNextStep();
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isUpdateScheduleSuccess) {
      completeAndGoNextStep();
    }
  }, [isUpdateScheduleSuccess]);

  const mergedTraits = traits ? [...traits.customer, ...traits.event] : undefined;

  const isStep2WithNoExpressions = currentStepId === 'expression' && (!stashedAudience?.expression || Object.keys(stashedAudience.expression).length === 0);

  const nextButton = (
    <div className='flex shrink-0 justify-end border-t border-gray-200 px-6 py-4'>
      <Button
        type='submit'
        className={isLastStep && 'bg-green-500'}
        onClick={handleNextButtonClicked}
        disabled={isStep2WithNoExpressions}
        isLoading={isLoading}
        text={t(!isLastStep ? 'next' : 'finish')}
      />
    </div>
  );

  return (
    <>
      {isModal ? (
        <AudienceModalContent
          createFormRef={createFormRef}
          onCreateFormSubmit={handleSubmit}
          steps={steps}
          stepId={currentStepId}
          currentStepIndex={currentStepIndex}
          syncFormRef={syncFormRef}
          traits={mergedTraits}
          audience={audienceForForm}
          onSuccess={onSuccess}
          closeModal={closeModal}
        >
          {!isLastStep && nextButton}
        </AudienceModalContent>
      ) : (
        <AudienceContent
          createFormRef={createFormRef}
          onCreateFormSubmit={handleSubmit}
          steps={steps}
          stepId={currentStepId}
          currentStepIndex={currentStepIndex}
          syncFormRef={syncFormRef}
          traits={mergedTraits}
          audience={audienceForForm}
        >
          {!isLastStep && nextButton}
        </AudienceContent>
      )}
    </>
  );
};
